import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Video } from "lucide-react";
import MatrixRain from "../components/MatrixRain";
import { Footer } from "../components/Sections";
import {
  Scheduler,
  TFInput,
  TFPhone,
  VISITOR_TZ,
  valueObj,
} from "../components/ApplicationFormSlides";
import { ASSETS, COUNTRY_CODES } from "../mock";
import {
  track,
  getVisitorId,
  getStoredUTM,
  usePageTracking,
} from "../analytics";
import { fireRegistrationConversion } from "../pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAME_Q = { id: "full_name", type: "short_text", placeholder: "First and last name" };
const EMAIL_Q = { id: "email", type: "email", placeholder: "you@email.com", autoFocus: false };
const PHONE_Q = { id: "phone", type: "phone_number", placeholder: "Phone number", autoFocus: false };

const WebinarBookingPage = () => {
  usePageTracking("webinar-booking");

  const [contact, setContact] = useState({ full_name: "", email: "", phone: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [bookedInfo, setBookedInfo] = useState(null);
  const [availRefresh, setAvailRefresh] = useState(0);
  const [selected, setSelected] = useState({ date: null, time: null });

  // No-index this funnel page.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Book Your Free Call | The Matrix Unlocked";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const validateContact = () => {
    const e = {};
    const name = String(contact.full_name || "").trim();
    if (name.split(/\s+/).filter(Boolean).length < 2)
      e.full_name = "Please enter your first and last name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(contact.email || "").trim()))
      e.email = "Please enter a valid email.";
    const digits = String(valueObj(contact.phone).number || "").replace(/\D/g, "");
    if (digits.length < 7) e.phone = "Please enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSlotSelect = (val) => {
    if (val.pickDate) {
      setSelected({ date: val.pickDate, time: null });
      return;
    }
    setSelected(val);
    if (!validateContact()) {
      // scroll contact fields into view
      document.getElementById("wb-contact")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    book(val);
  };

  const book = async (slot) => {
    if (submitting || done) return;
    setSubmitting(true);
    setErrors({});
    track("webinar_form_submit", {});
    const phone = valueObj(contact.phone);
    const phoneStr = `${phone.dial || "+1"} ${phone.number || ""}`.trim();
    const countryName =
      (COUNTRY_CODES.find((c) => c.code === phone.country) || {}).name || "";
    try {
      const booked = await axios.post(`${API}/calendly/book`, {
        start_time: slot.start_time,
        name: contact.full_name,
        email: contact.email,
        timezone: slot.timezone || VISITOR_TZ,
        phone: phoneStr,
        country: countryName,
      });
      const scheduledWithRefs = { ...slot, calendly: booked.data };
      setBookedInfo({
        ...booked.data,
        date: slot.date,
        time: slot.time,
        timezone: slot.timezone || VISITOR_TZ,
      });

      await axios.post(`${API}/applications`, {
        full_name: contact.full_name,
        email: contact.email,
        phone: phoneStr,
        scheduled_call: scheduledWithRefs,
        visitor_id: getVisitorId(),
        utm: getStoredUTM(),
      });

      const nameParts = String(contact.full_name || "").trim().split(/\s+/);
      // Call successfully booked: log to our backend. The Whop "call_booked"
      // conversion is fired SERVER-SIDE by POST /applications (whop_enrich_
      // application, event_id call_{app_id}) — do NOT also fire it client-side
      // here, or Whop counts the booking twice (different event_id => no dedupe).
      track("call_booked", {
        start_time: slot.start_time,
        timezone: slot.timezone || VISITOR_TZ,
        source: "webinar_booking",
      });
      fireRegistrationConversion({
        transactionId: booked.data?.event_uri ? `wb_${booked.data.event_uri}` : undefined,
        email: contact.email,
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        phone: phoneStr,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      const taken = status === 502 || status === 409;
      setErrors({
        scheduled_call: taken
          ? "That time was just booked by someone else — please pick another slot."
          : "Something went wrong booking your call. Please try again.",
      });
      setSelected({ date: slot.date, time: null });
      setAvailRefresh((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <header className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px]">
          <MatrixRain />
          <div className="matrix-fade" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-3xl flex-col items-center px-5 pt-12 pb-16">
          <img
            src={ASSETS.logoEmblem}
            alt="The Matrix Unlocked"
            className="h-16 w-auto sm:h-20"
          />
          <h1 className="mt-5 text-center text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-[44px]">
            Book Your <span className="text-[#16E36F]">Free Call</span> Below
          </h1>

          <div className="tf-card mt-8 w-full" id="typeform">
            <div className="tf-content">
              {done ? (
                <div
                  className="tf-step tf-anim-down mx-auto max-w-xl text-center"
                  data-testid="thankyou-screen"
                >
                  <h3 className="text-2xl font-semibold leading-snug text-white sm:text-3xl">
                    You&rsquo;re booked in! Your call with Joel is confirmed.
                  </h3>

                  {bookedInfo && (
                    <div className="tf-confirm-card" data-testid="booking-confirmation">
                      <div className="tf-confirm-row">
                        <CalendarCheck size={18} className="text-[#16E36F]" />
                        <span data-testid="booking-datetime">
                          {bookedInfo.start_time
                            ? new Date(bookedInfo.start_time).toLocaleString([], {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })
                            : `${bookedInfo.date} at ${bookedInfo.time}`}
                        </span>
                      </div>
                      <div className="tf-confirm-tz">
                        Your timezone ({(bookedInfo.timezone || "").replace(/_/g, " ")})
                      </div>
                      {bookedInfo.join_url && (
                        <a
                          href={bookedInfo.join_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tf-join-btn"
                          data-testid="booking-join-link"
                        >
                          <Video size={16} className="mr-2" />
                          Join Zoom Call
                        </a>
                      )}
                    </div>
                  )}

                  <p className="mt-5 text-neutral-400">
                    We&rsquo;ve emailed you the details. See you on the call!
                  </p>
                </div>
              ) : (
                <div className="tf-step mx-auto max-w-2xl">
                  <div id="wb-contact" className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-sm text-neutral-400">
                        Full name
                      </label>
                      <TFInput
                        question={NAME_Q}
                        value={contact.full_name}
                        onChange={(v) => setContact((p) => ({ ...p, full_name: v }))}
                        onEnter={() => {}}
                        error={errors.full_name}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm text-neutral-400">
                        Email
                      </label>
                      <TFInput
                        question={EMAIL_Q}
                        value={contact.email}
                        onChange={(v) => setContact((p) => ({ ...p, email: v }))}
                        onEnter={() => {}}
                        error={errors.email}
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm text-neutral-400">
                        Phone number
                      </label>
                      <TFPhone
                        question={PHONE_Q}
                        value={contact.phone}
                        onChange={(v) => setContact((p) => ({ ...p, phone: v }))}
                        onEnter={() => {}}
                        error={errors.phone}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <Scheduler
                      value={selected}
                      onSelectSlot={handleSlotSelect}
                      submitting={submitting}
                      refreshKey={availRefresh}
                    />
                    {errors.scheduled_call && (
                      <div className="tf-error">{errors.scheduled_call}</div>
                    )}
                    {submitting && (
                      <p className="mt-3 text-sm text-neutral-400">
                        Booking your call…
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default WebinarBookingPage;
